// ModelPane — the 3D half of Find in Sheets & Model, as a model.
//
// The pane used to be a list of anchors and nothing to look at, so a
// search could not show WHERE its answer sits and nobody could tell
// whether the model being searched was the one just pushed. This streams
// the project's live model (the same manifest the model viewer opens)
// and makes the search visible in it:
//
//   - every element behind the 3D hits is lit in orange; with "Only the
//     results" on, everything else is hidden
//   - the hit picked in the list is blue and the camera frames it
//   - clicking an element in the model asks the search about its type,
//     so the sheets side answers "where is this drawn"
//
// Hits are element TYPES on a level (utils/modelIngest); POST
// /anchors/uids expands them to their elements, once per result set.
//
// THE ELEMENT ITSELF (owner, 2026-10-29). Hovering shows what the pointer
// is on and clicking opens a floating panel with everything the export
// knows about that one element - its record and parameter groups, read the
// way the model view reads them (helpers/elementRecords). Before this the
// click was turned straight into its search TYPE and the element's own id
// was dropped, so no panel could show more than five fields about a group.
//
// F AND THE FRAME BUTTON frame what is lit. They read the viewer's
// selection, and this pane lights by highlighting, never by selecting -
// so both framed nothing (measured on staging main.9cf25c6d: the camera
// did not move). `pageSelection` is the viewer's door for a page that
// keeps its selection itself, the one construction tracking uses.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Switch, Tooltip } from "antd";
import { AimOutlined } from "@ant-design/icons";
import WebGlApp from "webgl";
import { liveManifestUrl } from "helpers/liveManifest";
import { expandAnchorUids } from "api/anchors/anchorApi";
import { boxForUids } from "pages/Tracking/stageHelpers";
import ViewerEffectsButton from "components/Viewer/ViewerEffectsButton";
import ViewerViewButtons from "components/Viewer/ViewerViewButtons";
import ViewerToolbar from "components/Viewer/ViewerToolbar";
import { attachBuildingGrid } from "helpers/buildingGridLoad";
import { createElementRecords } from "helpers/elementRecords";
import FloatingPanel from "./FloatingPanel";
import ElementProperties from "./ElementProperties";

const HIT_COLOR = "#fe6919";
const PICK_COLOR = "#2F6FED";
// Past this many lit elements the highlight still applies but pinning
// every one of them against the pixel floor would load the whole model.
const PIN_LIMIT = 20000;

// The hover card trails the pointer by this much and waits this long
// before asking for a record, so sweeping across the model asks nothing.
const HOVER_OFFSET = 16;
const HOVER_DELAY_MS = 220;

export default function ModelPane({
  projectId, hits = [], selected = null, onPickElement, modelInfo = null,
  // More to show in the floating panel under the element (the page's
  // tracking panel when the pane fills the window).
  panelExtra = null,
}) {
  const containerRef = useRef(null);
  const WebGL = useRef(null);
  const pollRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [noModel, setNoModel] = useState(false);
  // The result could not be framed: none of its elements turned up in the
  // model on screen. Said in words rather than left as an empty canvas.
  const [unframed, setUnframed] = useState(false);
  const [onlyResults, setOnlyResults] = useState(true);
  const [expansion, setExpansion] = useState({ uids: [], byAnchor: {}, capped: false });
  const [expanding, setExpanding] = useState(false);
  const paintedRef = useRef(new Set());
  const pickRef = useRef(onPickElement);
  pickRef.current = onPickElement;

  // ---- the element under the pointer, and the one clicked ----
  const records = useMemo(() => (projectId ? createElementRecords(projectId) : null), [projectId]);
  const [hover, setHover] = useState(null);      // { uid, x, y, info }
  const [picked, setPicked] = useState(null);    // { uid, info, loading }
  const pointer = useRef({ x: 0, y: 0 });
  const hoverTimer = useRef(null);
  const hoverUid = useRef("");
  const onHover = useCallback((uid) => {
    if (!uid || !records) return;
    const { x, y } = pointer.current;
    if (uid === hoverUid.current) { setHover((h) => (h ? { ...h, x, y } : h)); return; }
    hoverUid.current = uid;
    setHover({ uid, x, y, info: null });
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => {
      records.lookup(uid).then((info) => {
        if (hoverUid.current === uid) setHover((h) => (h && h.uid === uid ? { ...h, info } : h));
      }).catch(() => {});
    }, HOVER_DELAY_MS);
  }, [records]);
  const hoverOff = useCallback(() => {
    hoverUid.current = "";
    clearTimeout(hoverTimer.current);
    setHover(null);
  }, []);
  const onPick = useCallback((uid) => {
    hoverOff();
    if (!uid) { setPicked(null); return; }
    setPicked({ uid, info: null, loading: true });
    if (records) {
      records.lookup(uid)
        .then((info) => setPicked((p) => (p && p.uid === uid ? { uid, info, loading: false } : p)))
        .catch(() => setPicked((p) => (p && p.uid === uid ? { ...p, loading: false } : p)));
    }
    if (pickRef.current) pickRef.current(uid);
  }, [records, hoverOff]);
  const hoverRef = useRef(onHover); hoverRef.current = onHover;
  const hoverOffRef = useRef(hoverOff); hoverOffRef.current = hoverOff;
  const onPickRef = useRef(onPick); onPickRef.current = onPick;
  useEffect(() => () => clearTimeout(hoverTimer.current), []);
  // What F and the frame button act on: the clicked element, else the
  // picked result, else everything lit.
  const frameTargetRef = useRef([]);

  const viewer = () => WebGL.current && WebGL.current.viewer;
  const store = () => { const v = viewer(); return v && v.streaming && v.streaming.state; };

  // ---- the viewer, once per project ----
  useEffect(() => {
    if (!projectId || !containerRef.current) return undefined;
    const noop = () => {};
    setReady(false); setNoModel(false); setLoading(true);
    let cancelled = false;
    // The live version's own manifest, not the project-level copy a
    // promote rewrites (helpers/liveManifest) - the model the search
    // index was built from.
    liveManifestUrl(projectId).then((manifestUrl) => {
      if (cancelled || !containerRef.current) return;
      try {
        WebGL.current = new WebGlApp(
          containerRef.current,
          { glbs: [], manifestUrl },
          {
            setClickedChild: noop, setLoading, setError: noop, setPercentsLoaded: noop,
            setAllowToResize: noop, setModal: noop, setCoordsForPopover: noop,
            findInJsonUsingViewer: (uid) => onPickRef.current(uid || ""),
            setIsPopOver: (on) => { if (!on) hoverOffRef.current(); },
            onHoverMesh: (uid) => { if (uid) hoverRef.current(String(uid)); },
            pageSelection: () => frameTargetRef.current,
            notify: noop,
            selectedMesh: { current: null }, project: { _id: projectId },
          },
          null, false, false
        );
        attachBuildingGrid(WebGL.current.viewer, projectId);
      } catch (e) {
        setNoModel(true);
        return;
      }
      // The streaming session exists once the manifest has landed. Waited
      // for as long as it takes - a big model can take minutes - and a
      // missing model is only declared after a long quiet spell, and
      // withdrawn the moment it turns up after all.
      let tries = 0;
      pollRef.current = setInterval(() => {
        tries += 1;
        const v = viewer();
        if (v && v.streaming && v.streaming.state) {
          setReady(true); setNoModel(false);
          clearInterval(pollRef.current); pollRef.current = null;
          return;
        }
        if (tries === 120) setNoModel(true);          // 60 s of nothing
        if (tries > 1200) { clearInterval(pollRef.current); pollRef.current = null; }
      }, 500);
    });
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      try { if (WebGL.current && typeof WebGL.current.dispose === "function") WebGL.current.dispose(); } catch (e) { /* best effort */ }
      WebGL.current = null;
      paintedRef.current = new Set();
    };
  }, [projectId]);

  // The canvas follows its pane (split drag, expand, window resize).
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const v = viewer();
      try { if (v && typeof v.onWindowResize === "function") v.onWindowResize(); } catch (e) { /* best effort */ }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ---- expand the hits to their elements, once per result set ----
  const hitIds = useMemo(() => hits.map((h) => String(h._id)).slice(0, 500), [hits]);
  const hitKey = hitIds.join(",");
  useEffect(() => {
    let alive = true;
    if (!projectId || !hitIds.length) { setExpansion({ uids: [], byAnchor: {}, capped: false }); return undefined; }
    setExpanding(true);
    expandAnchorUids(projectId, hitIds)
      .then((r) => { if (alive) setExpansion({ uids: r.uids || [], byAnchor: r.byAnchor || {}, capped: !!r.capped }); })
      .catch(() => { if (alive) setExpansion({ uids: [], byAnchor: {}, capped: false }); })
      .finally(() => { if (alive) setExpanding(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, hitKey]);

  const selectedUids = useMemo(() => {
    if (!selected || selected.world !== "3d") return [];
    const range = expansion.byAnchor[String(selected._id)];
    if (range) return expansion.uids.slice(range[0], range[1]);
    return selected.uniqueId ? [selected.uniqueId] : [];
  }, [selected, expansion]);

  // ---- paint: results orange, the picked one blue, the rest hidden or left ----
  useEffect(() => {
    const st = store();
    if (!ready || !st) return;
    const v = viewer();
    const want = new Map();
    for (const u of expansion.uids) want.set(u, HIT_COLOR);
    for (const u of selectedUids) want.set(u, PICK_COLOR);
    if (picked && picked.uid) want.set(picked.uid, PICK_COLOR);
    const gone = [];
    paintedRef.current.forEach((u) => { if (!want.has(u)) gone.push(u); });
    try {
      if (gone.length && typeof st.bulkClearHighlights === "function") st.bulkClearHighlights(gone);
      if (want.size) st.bulkHighlight([...want.entries()]);
      if (onlyResults && expansion.uids.length) st.setIsolation(expansion.uids);
      else if (typeof st.clearIsolation === "function") st.clearIsolation();
      else st.setIsolation(null);
      const streaming = v && v.streaming;
      if (streaming && typeof streaming.setPinnedUids === "function") {
        const pin = selectedUids.length ? selectedUids : expansion.uids;
        streaming.setPinnedUids(pin.length <= PIN_LIMIT ? pin : pin.slice(0, PIN_LIMIT));
      }
    } catch (e) { /* the model still draws */ }
    paintedRef.current = new Set(want.keys());
  }, [ready, expansion, selectedUids, onlyResults, picked]);
  frameTargetRef.current = picked && picked.uid ? [picked.uid] : (selectedUids.length ? selectedUids : expansion.uids);

  // ---- frame: the picked hit, else the whole result ----
  const frame = useCallback((uids) => {
    const v = viewer();
    const c = v && v.streaming && v.streaming.controller;
    const adapter = c && c.adapter;
    if (!v || !v.cameraControls || !adapter || !adapter.activeUidOwner || !uids.length) return false;
    const box = boxForUids((uid) => {
      const o = adapter.activeUidOwner.get(uid);
      return o && o.record ? { mesh: o.record.mesh, index: o.record.index } : null;
    }, uids);
    if (!box) return false;
    try { v.cameraControls.fitToBox(box, true, { paddingTop: 0.2, paddingBottom: 0.2, paddingLeft: 0.2, paddingRight: 0.2 }); } catch (e) { return false; }
    return true;
  }, []);

  useEffect(() => {
    if (!ready) return undefined;
    const target = selectedUids.length ? selectedUids : expansion.uids;
    if (!target.length) return undefined;
    // Tiles for the target may still be arriving; try again a few times.
    setUnframed(false);
    let n = 0;
    const t = setInterval(() => {
      n += 1;
      if (frame(target)) { setUnframed(false); clearInterval(t); return; }
      if (n > 25) { setUnframed(true); clearInterval(t); }
    }, 900);
    if (frame(target)) clearInterval(t);
    return () => clearInterval(t);
  }, [ready, selectedUids, expansion, frame]);

  const lit = expansion.uids.length;
  const hoverHead = hover && hover.info && hover.info.record
    ? [hover.info.record.CategoryName, hover.info.record.FamilyName, hover.info.record.TypeName].filter(Boolean)
    : [];
  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%", background: "#f5f6f7" }}
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        pointer.current = { x: e.clientX - r.left, y: e.clientY - r.top };
      }}
      onMouseLeave={hoverOff}
    >
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />
      {hover && (!picked || picked.uid !== hover.uid) && (
        <div style={{
          position: "absolute", zIndex: 15, pointerEvents: "none", left: hover.x + HOVER_OFFSET, top: hover.y + HOVER_OFFSET,
          maxWidth: 300, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 9px",
          boxShadow: "0 4px 14px rgba(0,0,0,.15)", fontSize: 12,
        }}>
          {hoverHead.length ? (
            <>
              <div style={{ fontWeight: 600 }}>{hoverHead.join(" · ")}</div>
              <div style={{ color: "#666" }}>
                {[hover.info.record.Name, hover.info.record.Level || hover.info.record.LevelName].filter(Boolean).join(" · ")}
              </div>
              <div style={{ color: "#999", fontSize: 11, marginTop: 2 }}>Click for all its properties</div>
            </>
          ) : (
            <div style={{ color: "#888" }}>{hover.info ? (hover.info.error || "No record for this element") : "Reading…"}</div>
          )}
        </div>
      )}
      {picked && (
        <FloatingPanel title="Properties" onClose={() => setPicked(null)} testId="model-properties">
          <ElementProperties info={picked.info} loading={picked.loading} extra={panelExtra} />
        </FloatingPanel>
      )}
      <div style={{
        position: "absolute", left: 8, top: 8, right: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
        background: "#ffffffe6", border: "1px solid #eee", borderRadius: 6, padding: "4px 8px", fontSize: 12,
      }}>
        <span style={{ color: "#555" }}>
          {modelInfo && modelInfo.exportVersion ? `Live model ${modelInfo.exportVersion}` : "Live model"}
        </span>
        <Tooltip title="Hide everything that is not a result">
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Switch size="small" checked={onlyResults} onChange={setOnlyResults} /> Only the results
          </span>
        </Tooltip>
        <span style={{ color: lit ? "#fe6919" : "#999" }}>
          {expanding ? "finding the elements…" : hits.length ? `${lit.toLocaleString()} element${lit === 1 ? "" : "s"} lit${expansion.capped ? " (capped)" : ""}` : "search to light elements"}
        </span>
        <Tooltip title={picked ? "Frame the clicked element (F)" : selectedUids.length ? "Frame the picked result (F)" : "Frame the results (F)"}>
          <Button size="small" icon={<AimOutlined />} disabled={!ready || (!lit && !selectedUids.length && !picked)}
            onClick={() => frame(frameTargetRef.current)} style={{ marginLeft: "auto" }} />
        </Tooltip>
        <ViewerViewButtons viewer={() => WebGL.current && WebGL.current.viewer} variant="dark" />
        <ViewerEffectsButton viewer={() => WebGL.current && WebGL.current.viewer} variant="dark" style={{ background: "#f0f0f0", color: "#262626" }} />
      </div>
      {modelInfo && modelInfo.stale && (
        <div style={{ position: "absolute", left: 8, bottom: 8, right: 8, background: "#fff7e6", border: "1px solid #ffd591", borderRadius: 6, padding: "6px 10px", fontSize: 12, color: "#874d00" }}>
          A newer model push is live than the one the search was indexed from. The index is being rebuilt from it now; search again in a few minutes for results from the latest push.
        </div>
      )}
      {ready && unframed && lit > 0 && (
        <div data-testid="model-pane-unframed" style={{ position: "absolute", left: 8, top: 44, right: 8, background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "6px 10px", fontSize: 12, color: "#555" }}>
          These results have not appeared in the model shown here. The model may still be streaming in, or it was published after the search index was built; search again in a few minutes.
        </div>
      )}
      {!ready && (
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }}>
          <div style={{ fontSize: 12.5, color: "#777", background: "#ffffffcc", padding: "6px 10px", borderRadius: 6, textAlign: "center", maxWidth: 320 }}>
            {noModel
              ? "Still waiting for the model. A large model can take a few minutes; if this project has never been published from the Revit plugin there is nothing to show."
              : (loading ? "Loading the live model…" : "Starting the model…")}
          </div>
        </div>
      )}
      {/* THE SHARED TOOLBAR, and it is deliberately the LAST child.
          It positions itself absolutely against this container, so
          inside the header flex above it escaped that row and landed
          under the "Loading the live model" overlay - which paints
          after it in DOM order (Mark, headless on staging main.378ba59d:
          all four buttons reported covered). Last child, so nothing
          paints over it.
          gear={false} because this pane already renders its own
          ViewerEffectsButton in the header - two gears is the same
          duplication this change exists to remove. */}
{/* A CORNER OF ITS OWN. `viewer-zoom` only carries coordinates in
          the model-view and timeline stylesheets, so on this page the
          class is inert and the toolbar would sit wherever it fell in
          flow (Mark measured x=-41 on tracking, lying across twenty
          controls). A positioned wrapper gives it the same bottom-left
          corner the model view has. */}
      <div style={{ position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 5,
                   display: "flex", justifyContent: "flex-end", pointerEvents: "none" }}>
        <ViewerToolbar viewer={() => WebGL.current && WebGL.current.viewer} gear={false} />
      </div>
    </div>
  );
}
